import { City, Coordinates } from '../types';

/**
 * Converts degrees to radians for trigonometric operations.
 */
function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

/**
 * Calculates the great-circle distance between two coordinates using the haversine formula.
 * Returns kilometers to align with common UX expectations.
 */
export function distanceKm(a: Coordinates, b: Coordinates): number {
  const earthRadiusKm = 6371;
  const dLat = toRadians(b.lat - a.lat);
  const dLon = toRadians(b.lon - a.lon);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const haversine =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const arc = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(Math.max(0, 1 - haversine)));
  return earthRadiusKm * arc;
}

interface RankedCity {
  city: City;
  distance: number;
}

/**
 * Partitions an array in-place so that the smallest k elements (by a selector) occupy
 * the first k slots in no particular order. Uses an iterative quickselect to avoid
 * sorting the entire dataset when only the closest handful is needed.
 */
function quickselect<T>(items: T[], k: number, selector: (item: T) => number): void {
  let left = 0;
  let right = items.length - 1;

  while (left < right) {
    const pivotIndex = Math.floor((left + right) / 2);
    const pivotValue = selector(items[pivotIndex]);
    let i = left;
    let j = right;

    while (i <= j) {
      while (selector(items[i]) < pivotValue) i += 1;
      while (selector(items[j]) > pivotValue) j -= 1;
      if (i <= j) {
        [items[i], items[j]] = [items[j], items[i]];
        i += 1;
        j -= 1;
      }
    }

    if (k <= j) {
      right = j;
    } else if (k >= i) {
      left = i;
    } else {
      return;
    }
  }
}

/**
 * Selects the nearest cities to a target coordinate without sorting the full dataset.
 * Returns up to `limit` cities ordered from closest to farthest.
 */
export function selectClosestCities(cities: City[], target: Coordinates, limit = 100): City[] {
  if (limit <= 0) return [];

  const ranked: RankedCity[] = cities
    .filter((city) => typeof city.lat === 'number' && typeof city.lon === 'number')
    .map((city) => ({
      city,
      distance: distanceKm(target, { lat: city.lat as number, lon: city.lon as number }),
    }));

  if (ranked.length <= limit) {
    return ranked.sort((a, b) => a.distance - b.distance).map((entry) => entry.city);
  }

  quickselect(ranked, limit - 1, (item) => item.distance);
  const nearest = ranked.slice(0, limit);
  nearest.sort((a, b) => a.distance - b.distance);
  return nearest.map((entry) => entry.city);
}
